"use client";

import React, { useState, useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Avatar,
  Grid,
  Skeleton,
  IconButton,
} from "@mui/material";
import moment from "moment-timezone";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import CalendarPicker from "@/app/components/calendar/CalendarPicker";
import TimeSlotGrid from "@/app/components/calendar/TimeSlotGrid";

const SCHEDULE_CALENDAR_WIDTH = 350;

const ScheduleModalCalendar = ({
  isAvailabilityLoading,
  availability,
  cutoffDate,
  handleDaySelect,
  selectedDay,
  theme,
  timeSlots,
  selectedTime,
  isToday,
  handleTimeSelect,
  isMobile,
  meetingData,
  userTimezone,
  checkDayAvailability,
}) => {
  const [calendarView, setCalendarView] = useState("day");
  const [currentPage, setCurrentPage] = useState(0);
  const [isCalendarExpanded, setIsCalendarExpanded] = useState(false);
  const [weekStart, setWeekStart] = useState(() =>
    moment().tz(userTimezone).startOf("week"),
  );

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedDay]);

  const calculateDateLimits = () => {
    const now = moment().tz(userTimezone).startOf("month");
    let minDate = now.clone();
    let maxDate = now.clone().add(3, "months").endOf("month");

    if (availability && availability.times) {
      // Prefer dynamic window (days from today) over static end_date
      if (availability.window) {
        maxDate = moment().tz(userTimezone).add(availability.window, "days");
      } else if (availability.end_date) {
        const endDate = moment(availability.end_date);
        if (endDate.isValid() && endDate.isAfter(now)) {
          maxDate = endDate;
        }
      }
    }

    return { minDate, maxDate };
  };

  const { minDate, maxDate } = calculateDateLimits();
  const selectedMoment = selectedDay
    ? moment(selectedDay, "DD-MM-YYYY").tz(userTimezone)
    : moment().tz(userTimezone);

  useEffect(() => {
    if (selectedDay) {
      const selected = moment(selectedDay, "DD-MM-YYYY").tz(userTimezone);
      if (selected.isValid()) {
        setWeekStart(selected.clone().startOf("week"));
      }
      return;
    }
    setWeekStart(moment().tz(userTimezone).startOf("week"));
  }, [selectedDay, userTimezone]);

  useEffect(() => {
    if (isCalendarExpanded) {
      setCalendarView("day");
    }
  }, [isCalendarExpanded]);

  const weekDays = useMemo(() => {
    return Array.from({ length: 7 }).map((_, index) =>
      weekStart.clone().add(index, "days"),
    );
  }, [weekStart]);

  const weekRangeLabel = useMemo(() => {
    const start = weekStart.clone();
    const end = weekStart.clone().add(6, "days");
    if (start.year() === end.year()) {
      return `${start.format("MMM D")} - ${end.format("MMM D, YYYY")}`;
    }
    return `${start.format("MMM D, YYYY")} - ${end.format("MMM D, YYYY")}`;
  }, [weekStart]);

  const handlePrevWeek = () => {
    const currentWeekStart = moment().tz(userTimezone).startOf("week");
    setWeekStart((prev) => {
      const prevWeek = prev.clone().subtract(1, "week");
      return prevWeek.isBefore(currentWeekStart, "day") ? prev : prevWeek;
    });
  };

  const handleNextWeek = () => {
    setWeekStart((prev) => prev.clone().add(1, "week"));
  };

  const canGoPrevWeek = useMemo(() => {
    const currentWeekStart = moment().tz(userTimezone).startOf("week");
    return weekStart.isAfter(currentWeekStart, "day");
  }, [weekStart, userTimezone]);

  if (!availability?.available && !isAvailabilityLoading) {
    return (
      <Grid
        container
        sx={{
          justifyContent: "center",
          pt: 13,
        }}
      >
        <Typography
          variant="body1"
          color="error"
          align="center"
          sx={{
            mb: 2,
          }}
        >
          {meetingData?.sharer?.given_name} has not set available times.
        </Typography>
      </Grid>
    );
  }

  return (
    <>
      {/* Header */}
      <Grid
        container
        sx={{
          mb: { xs: 2, md: 3 },
          mt: { xs: 0, md: 1.5 },
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "flex-start", md: "center" },
          justifyContent: { xs: "flex-start", md: "space-between" },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
          }}
        >
          {isAvailabilityLoading ? (
            <>
              <Skeleton
                variant="circular"
                width={42}
                height={42}
                sx={{ mr: 2 }}
              />
              <Skeleton variant="text" width={200} height={24} />
            </>
          ) : (
            <>
              <Avatar
                src={
                  meetingData?.sharer?.picture_custom ||
                  meetingData?.sharer?.picture_cached ||
                  meetingData?.sharer?.picture
                }
                alt={`${meetingData?.sharer?.given_name} ${meetingData?.sharer?.family_name}`}
                sx={{ width: 42, height: 42, mr: 2 }}
              />
              <Typography variant={isMobile ? "h4b" : "body2"}>
                {meetingData?.sharer?.given_name}&apos;s availability
              </Typography>
            </>
          )}
        </Box>
      </Grid>
      {/* Calendar and Time Slots */}
      <Grid container spacing={{ xs: 2, md: 6, lg: 10 }}>
        {/* Calendar */}
        <Grid size={{ xs: 12, lg: 6 }}>
          <Box
            sx={{
              width: SCHEDULE_CALENDAR_WIDTH,
              maxWidth: "100%",
              mx: "auto",
            }}
          >
            {!isCalendarExpanded && (
              <>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2.5,
                  }}
                >
                  <IconButton
                    onClick={handlePrevWeek}
                    size="small"
                    disabled={!canGoPrevWeek}
                    sx={{ width: 36, height: 36 }}
                  >
                    <ChevronLeftIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: 500,
                      color: "text.primary",
                      textAlign: "center",
                      flex: 1,
                    }}
                  >
                    {weekRangeLabel}
                  </Typography>
                  <IconButton
                    onClick={handleNextWeek}
                    size="small"
                    sx={{ width: 36, height: 36 }}
                  >
                    <ChevronRightIcon sx={{ fontSize: 22 }} />
                  </IconButton>
                </Box>

                <Grid container columns={7} sx={{ mb: 2 }}>
                  {weekDays.map((day) => {
                    const dayKey = day.format("DD-MM-YYYY");
                    const isSelected = selectedDay === dayKey;
                    const isBlocked =
                      day.isBefore(minDate, "day") ||
                      day.isAfter(maxDate, "day") ||
                      !checkDayAvailability(day);

                    return (
                      <Grid key={dayKey} size={1}>
                        <Box
                          onClick={() => {
                            if (!isBlocked) {
                              handleDaySelect(day);
                            }
                          }}
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            py: 0.25,
                            cursor: isBlocked ? "default" : "pointer",
                            opacity: isBlocked ? 0.45 : 1,
                            userSelect: "none",
                          }}
                        >
                          <Typography
                            sx={{
                              fontSize: "12px",
                              fontWeight: 500,
                              color: "text.secondary",
                              textTransform: "uppercase",
                              lineHeight: 1.1,
                            }}
                          >
                            {day.format("ddd")}
                          </Typography>
                          <Box
                            sx={{
                              width: 28,
                              height: 28,
                              borderRadius: "50%",
                              mt: 1.8,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              backgroundColor: isSelected
                                ? theme.palette.primary.main
                                : "transparent",
                              color: isSelected ? "#fff" : "text.primary",
                              border: `1px solid ${isSelected ? theme.palette.primary.main : "transparent"}`,
                            }}
                          >
                            <Typography
                              sx={{
                                fontSize: "13px",
                                fontWeight: 400,
                                lineHeight: 1,
                              }}
                            >
                              {day.format("D")}
                            </Typography>
                          </Box>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              </>
            )}

            {!isCalendarExpanded && (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "flex-end",
                  mb: 2,
                }}
              >
                <Box
                  onClick={() => setIsCalendarExpanded(true)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.25,
                    cursor: "pointer",
                    color: "text.secondary",
                    userSelect: "none",
                    "&:hover": {
                      color: "text.primary",
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "12px",
                      fontWeight: 500,
                      lineHeight: 1,
                    }}
                  >
                    Expand calendar
                  </Typography>
                  <KeyboardArrowDownIcon sx={{ fontSize: 16 }} />
                </Box>
              </Box>
            )}

            {isCalendarExpanded && (
              <Box sx={{ mt: 1, mb: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "center", m: 0 }}>
                  <CalendarPicker
                    theme={theme}
                    selectedMoment={selectedMoment}
                    onDateChange={handleDaySelect}
                    minDate={minDate}
                    maxDate={maxDate}
                    calendarView={calendarView}
                    onViewChange={setCalendarView}
                    onMonthChange={() => {
                      setTimeout(() => setCalendarView("day"), 0);
                    }}
                    selectedDay={selectedDay}
                    availability={availability}
                    cutoffDate={cutoffDate}
                    meetingDuration={meetingData?.session?.duration}
                    userTimezone={userTimezone}
                    checkDayAvailability={checkDayAvailability}
                    isLoading={isAvailabilityLoading}
                    headerLabelFontSize="16px"
                    weekDayLabelFontSize="12px"
                    weekDayLabelFontWeight={500}
                    dayOfWeekFormatter={(date) => date.format("ddd")}
                    showAvailabilityDot={false}
                    dayNumberFontSize="13px"
                    dayNumberFontWeight={400}
                    sx={{
                      width: SCHEDULE_CALENDAR_WIDTH,
                      maxWidth: "100%",
                      mx: "auto",
                      "& .MuiDateCalendar-root": {
                        width: "100%",
                        maxWidth: "none",
                        margin: 0,
                        paddingLeft: 0,
                        paddingRight: 0,
                      },
                      "& .MuiPickersCalendarHeader-root": {
                        paddingLeft: 0,
                        paddingRight: 0,
                      },
                      "& .MuiDayCalendar-header": {
                        marginLeft: 0,
                        marginRight: 0,
                        justifyContent: "space-between",
                      },
                      "& .MuiDayCalendar-weekDayLabel": {
                        color: "text.secondary",
                        textTransform: "uppercase",
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: 1.1,
                      },
                      "& .MuiDayCalendar-weekContainer": {
                        marginLeft: 0,
                        marginRight: 0,
                        justifyContent: "space-between",
                      },
                      "& .MuiPickersDay-root": {
                        width: 28,
                        height: 28,
                        minWidth: 28,
                        fontSize: "13px!important",
                        fontWeight: "400!important",
                        lineHeight: 1,
                      },
                    }}
                  />
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    mt: 1,
                  }}
                >
                  <Box
                    onClick={() => setIsCalendarExpanded(false)}
                    sx={{
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.25,
                      cursor: "pointer",
                      color: "text.secondary",
                      userSelect: "none",
                      "&:hover": {
                        color: "text.primary",
                      },
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        fontWeight: 500,
                        lineHeight: 1,
                      }}
                    >
                      Collapse calendar
                    </Typography>
                    <KeyboardArrowUpIcon sx={{ fontSize: 16 }} />
                  </Box>
                </Box>
              </Box>
            )}
          </Box>
        </Grid>

        {/* Time Slots */}
        <Grid size={{ xs: 12, lg: 6 }} sx={{ pl: { lg: 2 } }}>
          {isAvailabilityLoading ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Skeleton variant="text" width={180} height={24} sx={{ mb: 1 }} />
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "repeat(2, 1fr)",
                    md: "repeat(2, 1fr)",
                  },
                  gap: 1,
                }}
              >
                {Array.from({ length: isMobile ? 6 : 8 }).map((_, i) => (
                  <Skeleton
                    key={`slot-${i}`}
                    variant="rectangular"
                    width="100%"
                    height={36}
                    sx={{ borderRadius: 0.5 }}
                  />
                ))}
              </Box>
              {/* Pagination skeleton */}
              {/* <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mt: 2,
                  gap: 1,
                }}
              >
                <Skeleton variant="circular" width={28} height={28} />
                <Skeleton variant="text" width={50} height={16} />
                <Skeleton variant="circular" width={28} height={28} />
              </Box> */}
            </Box>
          ) : (
            <TimeSlotGrid
              theme={theme}
              timeSlots={timeSlots}
              selectedTime={selectedTime}
              isToday={isToday}
              handleTimeSelect={handleTimeSelect}
              userTimezone={userTimezone}
              selectedDay={selectedDay}
              duration={meetingData?.session?.duration}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              slotsPerPage={6}
              slotColumnSize={4}
              slotCardSx={{ borderRadius: "10px" }}
              slotTextSx={{ fontSize: "13px", fontWeight: 400 }}
              showPagination={true}
              paginationSx={{ mb: { xs: 0, sm: 2.5 } }}
              timezonePrefixText="In your time zone,"
              timezoneSectionSx={{ mt: { xs: 0, sm: 1.5 }, mb: 1.5 }}
              timezoneSelectorSx={{
                "& .MuiTypography-root": {
                  fontSize: "12px!important",
                  color: "text.secondary",
                  fontWeight: 400,
                },
                "& .MuiSvgIcon-root": {
                  display: "none",
                },
              }}
            />
          )}
        </Grid>
      </Grid>
    </>
  );
};

export default ScheduleModalCalendar;
